import { Height, Waist, Weight, Smoking } from "../entities/quote";
import {
    AboutYouStore,
    MeasurementUnitLength,
    MeasurementUnitType,
    MeasurementUnitWeight,
    SmokerStatus,
} from "../stores/aboutYou";
import { AboutYouDependantStore } from "../stores/dependantAboutYou";
import { convertHeightToInches, convertWeightToPounds } from "../utils/quote";
import { Gender, Title } from "../stores/yourDetails";

export const aboutLifeStyleInformationForQuote = (state: AboutYouStore | AboutYouDependantStore) :
    {
        alcoholUnitsPerWeek: number | undefined,
        height: Height | undefined,
        weight: Weight | undefined,
        waist: Waist | undefined,
    } | {
        alcoholUnitsPerWeek: number | undefined,
        height: Height | undefined,
        weight: Weight | undefined,
        waist: Waist | undefined,
    } & Smoking => {
    const height: Height | undefined = formattedHeight(state);
    const waist: Waist = formattedWaist(state);
    const weight: Weight | undefined = formattedWeight(state);
    const smokerBlock : Smoking | undefined = state.smoker !== null
        ? state.smoker === SmokerStatus.Never
            ? {
                smoker: SmokerStatus.Never,
            }
            : state.smoker === SmokerStatus.Previously
                ? {
                    smoker: SmokerStatus.Previously,
                    smoking: {
                        started: state.smokingStart,
                        ended: state.smokingEnd,
                        cigarettesPerDay: state.cigarettesPerDay ?? 0,
                        cigarsPerDay: state.cigarsPerDay ?? 0,
                        rollingTobaccoGramsPerWeek: state.rollingTobaccoPerWeek ?? 0,
                        pipeTobaccoGramsPerWeek: state.pipeTobaccoPerWeek ?? 0,
                    },
                }
                : {
                    smoker: SmokerStatus.Currently,
                    smoking: {
                        started: state.smokingStart,
                        cigarettesPerDay: state.cigarettesPerDay ?? 0,
                        cigarsPerDay: state.cigarsPerDay ?? 0,
                        rollingTobaccoGramsPerWeek: state.rollingTobaccoPerWeek ?? 0,
                        pipeTobaccoGramsPerWeek: state.pipeTobaccoPerWeek ?? 0,
                    },
                }
        : undefined;
    return {
        ...smokerBlock,
        alcoholUnitsPerWeek: state.alcohol ?? undefined,
        height,
        weight,
        waist,
    };
};

export type LifestyleInformation = {
    alcoholUnitsPerWeek: number | undefined,
    height: Height,
    weight: Weight,
    waist: Waist,
};

export const generateSmokerBlock = (state: AboutYouStore | AboutYouDependantStore) : Smoking => {
    switch (state.smoker) {
        case SmokerStatus.Previously:
            return {
                smoker: SmokerStatus.Previously,
                smoking: {
                    started: state.smokingStart,
                    ended: state.smokingEnd,
                    cigarettesPerDay: state.cigarettesPerDay ?? 0,
                    cigarsPerDay: state.cigarsPerDay ?? 0,
                    rollingTobaccoGramsPerWeek: state.rollingTobaccoPerWeek ?? 0,
                    pipeTobaccoGramsPerWeek: state.pipeTobaccoPerWeek ?? 0,
                },
            };
        case SmokerStatus.Currently:
            return {
                smoker: SmokerStatus.Currently,
                smoking: {
                    started: state.smokingStart,
                    cigarettesPerDay: state.cigarettesPerDay ?? 0,
                    cigarsPerDay: state.cigarsPerDay ?? 0,
                    rollingTobaccoGramsPerWeek: state.rollingTobaccoPerWeek ?? 0,
                    pipeTobaccoGramsPerWeek: state.pipeTobaccoPerWeek ?? 0,
                },
            };
        case SmokerStatus.Never:
            return { smoker: SmokerStatus.Never };
        default:
            return { smoker: SmokerStatus.Never };
    }
};

export type LifestyleInformationWithSmoking = LifestyleInformation & Smoking;
export const aboutLifeStyleInformationPartial = (state: AboutYouStore | AboutYouDependantStore) :
    Partial<LifestyleInformationWithSmoking> => {
    const smokerStatus = state.smoker;
    const smokerBlock = smokerStatus !== null ? generateSmokerBlock(state) : undefined;
    return {
        ...smokerBlock,
        alcoholUnitsPerWeek: state.alcohol ?? undefined,
        height: formattedHeight(state),
        weight: formattedWeight(state),
        waist: formattedWaist(state),
    };
};

export const aboutLifeStyleInformation = (state: AboutYouStore | AboutYouDependantStore) :
LifestyleInformationWithSmoking | undefined => {
    if (state.smoker === null) {
        return undefined;
    }
    const smokerBlock = generateSmokerBlock(state);
    return {
        ...smokerBlock,
        alcoholUnitsPerWeek: state.alcohol ?? undefined,
        height: formattedHeight(state),
        weight: formattedWeight(state),
        waist: formattedWaist(state),
    };
};

const formattedHeight = (state: AboutYouStore | AboutYouDependantStore) : Height | undefined => {
    if (state.heightUnit === MeasurementUnitType.Imperial &&
        typeof state.heightFeet === "number" && typeof state.heightInches === "number") {
        return {
            unit: MeasurementUnitLength.Inches,
            value: convertHeightToInches(state.heightFeet, state.heightInches),
        };
    }
    if (state.heightUnit === MeasurementUnitType.Metric && typeof state.heightCentimetres === "number") {
        return {
            unit: MeasurementUnitLength.Centimetres,
            value: Number(state.heightCentimetres),
        };
    }
    return undefined;
};

const formattedWeight = (state: AboutYouStore | AboutYouDependantStore) : Weight | undefined => {
    if (state.weightUnit === MeasurementUnitType.Imperial &&
        typeof state.weightStone === "number" && typeof state.weightPounds === "number") {
        return {
            unit: MeasurementUnitWeight.Pounds,
            value: convertWeightToPounds(state.weightStone, state.weightPounds),
        };
    }
    if (state.weightUnit === MeasurementUnitType.Metric && typeof state.weightKg === "number") {
        return {
            unit: MeasurementUnitWeight.Kilogram,
            value: state.weightKg,
        };
    }
    return undefined;
};

export const formattedWaist = (state: AboutYouStore | AboutYouDependantStore) : Waist | undefined => {
    if (state.waistUnit === MeasurementUnitType.Imperial && typeof state.waistInches === "number") {
        return {
            unit: MeasurementUnitLength.Inches,
            value: state.waistInches,
        };
    }
    if (state.waistUnit === MeasurementUnitType.Metric && typeof state.waistCentimetres === "number") {
        return {
            unit: MeasurementUnitLength.Centimetres,
            value: state.waistCentimetres,
        };
    }
    return undefined;
};

export const selectingWeightUnitWillClearTheOther = (state: AboutYouStore | AboutYouDependantStore) : void => {
    if (state.weightUnit === MeasurementUnitType.Imperial) {
        state.weightKg = null;
    } else {
        state.weightStone = null;
        state.weightPounds = null;
    }
};

export const selectingHeightUnitWillClearTheOther = (state: AboutYouStore | AboutYouDependantStore) : void => {
    if (state.heightUnit === MeasurementUnitType.Imperial) {
        state.heightCentimetres = null;
    } else {
        state.heightFeet = null;
        state.heightInches = null;
    }
};

export const selectingWaistUnitWillClearTheOther = (state: AboutYouStore | AboutYouDependantStore) : void => {
    if (state.waistUnit === MeasurementUnitType.Imperial) {
        state.waistCentimetres = null;
    } else {
        state.waistInches = null;
    }
};

export const gender = (title: Title | null): Gender | null => {
    switch (title) {
        case Title.Mr:
        case Title.Sir:
        case Title.DrMale:
            return Gender.Male;
        case Title.Ms:
        case Title.Mrs:
        case Title.Miss:
        case Title.DrFemale:
            return Gender.Female;
        default:
            return null;
    }
};
